import { memo } from 'react';

import { BooleanControl, type BooleanControlProps } from './BooleanControl';
import { LayoutControl, type LayoutControlProps } from './LayoutControl';
import { NumberControl, type NumberControlProps } from './NumberControl';
import { SelectControl, type SelectControlProps } from './SelectControl';
import { SliderControl, type SliderControlProps } from './SliderControl';
import { TextControl, type TextControlProps } from './TextControl';

export interface ControlProps<V> {
  name?: string;
  label?: string;
  hidden?: boolean;
  description?: string;
  value?: V;
  defaultValue?: V;
  // onChange?: (value: V) => void;
}

export type ControlsProps<K extends string = string> =
  | BooleanControlProps
  | TextControlProps
  | SelectControlProps<K>
  | SliderControlProps
  | LayoutControlProps
  | NumberControlProps;

export const SettingControl = memo<ControlsProps>(props => {
  if (props.hidden) {
    return null;
  }
  switch (props.type) {
    case 'string':
      return (
        <TextControl
          name={props.name}
          label={props.label}
          description={props.description}
          value={props.value}
          onChange={props.onChange}
        />
      );

    case 'number':
      return (
        <NumberControl
          name={props.name}
          label={props.label}
          description={props.description}
          value={props.value}
          min={props.min}
          max={props.max}
          step={props.step}
          onChange={props.onChange}
        />
      );

    case 'boolean':
      return (
        <BooleanControl
          name={props.name}
          label={props.label}
          value={props.value}
          defaultChecked={props.defaultValue}
          onChange={props.onChange}
        />
      );

    case 'select':
      return (
        <SelectControl
          name={props.name}
          label={props.label}
          value={props.value}
          options={props.options}
          multiple={props.multiple}
          onChange={props.onChange}
        />
      );

    case 'slider':
      return (
        <SliderControl
          name={props.name}
          label={props.label}
          description={props.description}
          value={props.value}
          min={props.min}
          max={props.max}
          step={props.step}
          onChange={props.onChange}
        />
      );

    case 'layout':
      return (
        <LayoutControl
          value={props.value}
          onChange={props.onChange}
          name={props.name}
          label={props.label}
          description={props.description}
        />
      );
  }
});

SettingControl.displayName = 'SettingControl';
